import { Component, Inject, OnInit, Optional, Signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CategoryElement } from '@core/data/partial/categories.model';
import { SnackBarService } from '@core/services/snackbar.service';
import { DictionariesCollectionService } from '@core/data/dictionaries/dictionaries-collection.service';
import { AccountApiService } from '@core/services/api/account-api.service';
import { userInformation } from '@core/services/user-information';
import { SupportApiService } from '@core/services/api/support-api.service';
import { ConsentUrlsService } from '../../../../core/data/consent-urls/services/consent-urls.service';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { MatError } from '@angular/material/form-field';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConsentUrlsAdapted } from '@core/data/consent-urls/models/consent-urls-adapted.model';

import {
  userEmailSelector,
  userFullNameSelector,
} from '@core/store/selectors/user-data.selectors';

import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';

import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  DialogData,
  DropdownComponent,
  InputFieldModule,
  TextfieldModule,
  XtDialogComponent,
} from '@xt/ui-kit';
import { ButtonModule } from '@shared/components/atoms/button/button.module';

@Component({
  selector: 'xt-portal-contact-form-dialog',
  templateUrl: './contact-form-dialog.component.html',
  styleUrls: ['./contact-form-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    XtDialogComponent,
    ButtonModule,
    DropdownComponent,
    InputFieldModule,
    TextfieldModule,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    CypressDirectiveModule,
    MatError,
  ],
})
export class ContactFormDialogComponent implements OnInit {
  dialogConfig: DialogData;
  fixedSubject: string;
  preselectedIds: string[] = [];
  submitForm: FormGroup;
  availableTags = [];
  user = {
    name: '',
    email: '',
    phone: '',
  };
  isDropdownTouched = false;
  availableSubject$: Observable<CategoryElement[]>;
  availableTagsReady = false;

  private userInfo = userInformation();

  public consents: Signal<ConsentUrlsAdapted> = toSignal(
    this.consentUrlsService.consents$,
  );

  constructor(
    public dialogRef: MatDialogRef<ContactFormDialogComponent>,
    private supportApiService: SupportApiService,
    private accountService: AccountApiService,
    private dictionariesCollection: DictionariesCollectionService,
    private consentUrlsService: ConsentUrlsService,
    private fb: FormBuilder,
    private sb: SnackBarService,
    private store: Store,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.fixedSubject = this.data?.subject;
  }

  ngOnInit(): void {
    this.getUserData();
    this.getTags();
    this.buildForm();

    this.dialogConfig = {
      title: 'app.dialog.infoForm.title',
    };
  }

  /**
   * Get user information
   */
  getUserData() {
    this.store
      .pipe(select(userEmailSelector))
      .subscribe((email) => (this.user['email'] = email));

    this.store
      .pipe(select(userFullNameSelector))
      .subscribe((name) => (this.user['name'] = name));
  }

  /**
   * Build reactive form
   */
  buildForm() {
    this.submitForm = this.fb.group({
      username: [
        { value: this.user.name, disabled: !!this.user.name.length },
        [Validators.required],
      ],
      email: [
        { value: this.user.email, disabled: true },
        [Validators.required, Validators.email],
      ],
      subject: [[], [Validators.required]],
      phone: [
        null,
        Validators.compose([
          Validators.pattern('[- +()0-9]+'),
          Validators.minLength(4),
        ]),
      ],
      message: [null, [Validators.required]],
    });

    if (this.fixedSubject) {
      this.preselectedIds = [this.fixedSubject];
      this.setTags(this.preselectedIds);
    }
  }

  /**
   * Get all available tags
   */
  getTags() {
    if (this.fixedSubject) {
      // if subject is set
      this.availableTags = [{ id: this.fixedSubject, name: this.fixedSubject }];
      this.availableSubject$ = of([
        { id: this.fixedSubject, name: this.fixedSubject },
      ]);
      this.availableSubject$.subscribe(() => {
        this.availableTagsReady = true;
      });
    } else {
      this.availableSubject$ = this.dictionariesCollection
        .getItemById$('taxonomytermsupporttopics')
        .pipe(
          map((dict) => dict.list),
          take(2),
        );

      this.availableSubject$.subscribe(() => {
        this.availableTagsReady = true;
      });
      // else read available Options
      this.dictionariesCollection
        .getItemById$('taxonomytermsupporttopics')
        .subscribe((res) => {
          this.availableTags = res.list
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name));
        });
    }
  }

  /**
   * closes the mat dialog
   */
  closeDialog(): void {
    this.dialogRef.close();
  }

  /**
   * Set tags values to form
   */
  setTags(values): void {
    this.submitForm.controls.subject.setValue(values);
  }

  /**
   * Detect when the dropdown is touched
   */
  touched() {
    this.isDropdownTouched = true;
  }

  /**
   * Submit form
   */
  sendRequest() {
    this.submitForm.markAllAsTouched();
    if (!this.submitForm.valid) return;

    const form = this.submitForm.getRawValue();

    const payload = this.createPayload(form);

    if (this.data?.isDataExportRequest) {
      this.requestDataExport(payload);
    } else {
      this.sendSupportRequest(payload);
    }
  }

  sendSupportRequest(payload: any) {
    this.supportApiService.sendRequest('message', payload).subscribe((res) => {
      this.sb.success('app.dialog.infoForm.submittedSuccessfully');
      this.closeDialog();
    });
  }

  requestDataExport(payload: any) {
    this.accountService.exportData(payload).subscribe({
      next: () => {
        this.sb.success('app.dialog.infoForm.submittedSuccessfully');
        this.closeDialog();
      },
      error: (error) => {
        if (error.status == '202') {
          this.sb.success('app.dialog.infoForm.submittedSuccessfully');
          this.closeDialog();
        } else {
          this.sb.error('app.errors.default');
          this.closeDialog();
        }
      },
    });
  }

  createPayload(form: any) {
    const { email, username, subject, message, phone } = form;
    if (this.data?.isDataExportRequest) {
      const representedAccountId =
        this.userInfo().patientRepresentation?.patient?.patientInternalId;
      return {
        message,
        subject: subject?.map((s) => s.name).join(' ') || '',
        phone_number: phone,
        represented_account_id: representedAccountId,
      };
    } else {
      return {
        sender_address: email,
        message,
        subject: subject?.map((s) => s.name).join(' ') || '',
        customer_name: username,
        customer_phone_number: phone,
      };
    }
  }
}
